import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRightLeft,
  ChevronRight,
  FilterX,
  History,
  Landmark,
  Loader2,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';
import ExpenseModal from './ExpenseModal';
import SettlementModal from './SettlementModal';
import ExpenseDeleteDialog from './ExpenseDeleteDialog';
import RecentlyDeletedDrawer from './RecentlyDeletedDrawer';
import { HistoryEventList } from './expenseHistory';
import { describeEntryType, formatMoney, isConversionLeg } from './expenseFormat';

const defaultExpenseForm = {
  occurred_on: new Date().toISOString().split('T')[0],
  description: '',
  category: 'General',
  total_cost: '',
  currency: 'CAD',
  split_type: 'equal',
  paid_by: 'navjeev',
  kate_share: '',
  nav_share: '',
  kate_percentage: '',
  nav_percentage: '',
  notes: '',
};

const defaultSettlementForm = {
  occurred_on: new Date().toISOString().split('T')[0],
  amount: '',
  currency: '',
  paid_by: '',
  paid_to: '',
  notes: '',
};

const getCurrencyTone = (currency) => {
  const tones = {
    CAD: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    USD: 'border-sky-200 bg-sky-50 text-sky-700',
    EUR: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    SGD: 'border-amber-200 bg-amber-50 text-amber-700',
    GBP: 'border-purple-200 bg-purple-50 text-purple-700',
    MXN: 'border-orange-200 bg-orange-50 text-orange-700',
  };
  return tones[currency] || 'border-slate-200 bg-slate-50 text-slate-700';
};


const getRecordTone = (record) => (
  record.entry_type === 'payment'
    ? {
        badge: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
        panel: 'border-emerald-200/70 bg-emerald-50/70',
        icon: 'bg-emerald-500 text-white',
      }
    : {
        badge: 'bg-slate-100 text-slate-700 ring-slate-200',
        panel: 'border-slate-200/80 bg-white/90',
        icon: 'bg-slate-900 text-white',
      }
);

const getTransactionSummary = (record) => {
  if (record.entry_type === 'payment') {
    const payer = record.shares.find((s) => Number(s.balance_delta || 0) >= 0);
    const receiver = record.shares.find((s) => Number(s.balance_delta || 0) < 0);
    if (payer && receiver) {
      return `${payer.member.display_name} paid ${receiver.member.display_name} ${formatMoney(record.currency, Math.abs(Number(payer.balance_delta || 0)))}`;
    }
    return null;
  }
  // Expense: "Kate paid · Nav owes CA$30.00"
  const payer = record.paid_by_member;
  if (!payer || !record.shares || record.shares.length < 2) return null;
  const other = record.shares.find((s) => s.member.slug !== payer.slug);
  if (!other) return null;
  const owedAmount = Math.abs(Number(other.share_amount ?? other.balance_delta ?? 0));
  return { paidBy: payer.display_name, owes: other.member.display_name, amount: formatMoney(record.currency, owedAmount) };
};

// --- The compact ledger --------------------------------------------------
//
// The ledger used to render every loaded record as a tall card, and to keep a
// permanently-mounted search box and three filter selects above them. On a
// phone that meant scrolling past a filter bar nobody had asked for to reach a
// list whose first four rows filled the screen.
//
// So: ten compact rows by default, the controls behind one toggle, and a
// "show all" that means all. Nothing a row used to carry is gone -- the amount,
// currency, date, category and who paid are all still on the row, and Details
// (which carries the immutable history), Edit and Delete are still one tap
// away from it.
const COMPACT_LIMIT = 10;

// Filtering is client-side over the loaded window, deliberately. The server's
// `search` only ever matched `description`; searching "Kate" or "Groceries"
// returned nothing. Matching here costs one pass over at most `FETCH_LIMIT`
// rows and can look at the category and both members, and -- because the whole
// candidate set is in hand -- it can honestly say how many matches there are.
const FETCH_LIMIT = 200;

const ENTRY_TYPE_FILTERS = [
  { value: '', label: 'All types' },
  { value: 'expense', label: 'Expenses' },
  { value: 'payment', label: 'Settlements' },
  { value: 'conversion', label: 'Currency conversions' },
];

const matchesEntryType = (record, filter) => {
  if (!filter) return true;
  // A conversion is a `payment` leg plus an `fx_settlement` leg; both answer to
  // "conversion" and neither should show up under "settlements", or the filter
  // would quietly disagree with the label the row itself carries.
  if (filter === 'conversion') return isConversionLeg(record);
  if (filter === 'payment') return record.entry_type === 'payment' && !isConversionLeg(record);
  return record.entry_type === filter && !isConversionLeg(record);
};

// Deliberately the PAYER and PAYEE, not every member on the record's shares.
// Both members are on the shares of every equally-split expense, so folding
// those in would make "Kate" match the entire ledger -- a search that matches
// everything is the same as no search. Searching a name means "the ones this
// person paid for or was paid by".
const recordSearchHaystack = (record) => ([
  record.description,
  record.category,
  record.currency,
  describeEntryType(record),
  record.paid_by_member?.display_name,
  record.paid_to_member?.display_name,
].filter(Boolean).join(' ').toLowerCase());

const matchesSearch = (record, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return recordSearchHaystack(record).includes(needle);
};

/**
 * One transaction, compactly.
 *
 * The row itself is the Details/History control -- a whole-row target rather
 * than a 44px button in a corner -- with Edit and Delete beside it. Their
 * labels collapse to icons under `sm`, but their accessible names do not: the
 * Delete name still reads "Delete Ramen night, CAD 80.00", because a
 * destructive control that announces itself as "Delete" and nothing else is
 * the same ambiguity on a screen reader that a bare trash icon is on screen.
 */
const LedgerRow = ({ record, onDetails, onEdit, onDelete }) => {
  const tone = getRecordTone(record);
  const summary = getTransactionSummary(record);
  const who = typeof summary === 'string'
    ? summary
    : summary
      ? `${summary.paidBy} paid · ${summary.owes} owes ${summary.amount}`
      : '';
  const money = formatMoney(record.currency, record.total_cost || 0);

  return (
    /* `min-w-0` is load-bearing, not decoration: a grid item defaults to
       `min-width: auto`, so without it the row refuses to shrink below the
       min-content width of the description + amount and pushes the whole page
       into a horizontal scroll (measured: a 390px viewport scrolling to
       631px). Every ancestor of a `truncate` has to be allowed to be narrow. */
    <li className={clsx('flex min-w-0 items-stretch gap-1 rounded-[22px] border shadow-sm transition hover:shadow-md', tone.panel)}>
      <button
        type="button"
        onClick={() => onDetails(record)}
        aria-label={`Details and history for ${record.description}`}
        className="flex min-h-[56px] min-w-0 flex-1 items-center gap-2.5 rounded-[22px] px-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:gap-3"
      >
        <span className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', tone.icon)} aria-hidden="true">
          {record.entry_type === 'expense' ? <ReceiptText className="h-4 w-4" /> : <ArrowRightLeft className="h-4 w-4" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-900">{record.description}</span>
          <span className="mt-0.5 block truncate text-xs text-slate-500">
            {/* Under `sm` the amount leads the meta line instead of sitting in
                a right-hand column. Two 44px action buttons plus a column wide
                enough for "CAD 12.50" left the description about 90px on a
                390px screen -- "Test tr…", which identifies nothing. The
                amount goes FIRST here so it is the one thing the truncation
                can never eat. */}
            <span className="font-semibold text-slate-700 sm:hidden">
              {money}
              {describeEntryType(record) === 'Expense' ? '' : ` ${describeEntryType(record).toLowerCase()}`}
              {' · '}
            </span>
            {record.occurred_on} · {record.category || 'Uncategorized'}{who ? ` · ${who}` : ''}
          </span>
        </span>
        <span className="hidden shrink-0 text-right sm:block">
          <span className="block text-sm font-semibold text-slate-900">{money}</span>
          <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {describeEntryType(record)}
          </span>
        </span>
        <ChevronRight className="hidden h-4 w-4 shrink-0 text-slate-300 sm:block" aria-hidden="true" />
      </button>
      <div className="flex shrink-0 items-center gap-1 py-1 pr-2">
        <button
          type="button"
          onClick={() => onEdit(record)}
          aria-label={`Edit ${record.description}`}
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:px-3"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Edit</span>
        </button>
        <button
          type="button"
          onClick={() => onDelete(record)}
          aria-label={`Delete ${record.description}, ${money}`}
          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2 text-sm font-semibold text-rose-600 transition hover:border-rose-300 hover:bg-rose-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 sm:px-3"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Delete</span>
        </button>
      </div>
    </li>
  );
};

// --- Transaction history -------------------------------------------------
// The API returns immutable audit events with exact before/after snapshots.
// The UI shows a COMPACT reading of them: one line per event, plus the
// field-level changes the server already derived. Deliberately reachable only
// from a transaction's detail sheet -- putting history on every ledger card
// would bury the ledger under an audit log nobody asked to read. The rendering
// itself lives in expenseHistory.jsx, shared with the recovery drawer so a
// deleted entry's trail reads identically to a live one's.

const currencyOrder = ['CAD', 'USD', 'EUR', 'SGD'];

const sortBalances = (balances) => (
  [...balances].sort((left, right) => {
    const leftIndex = currencyOrder.indexOf(left.currency);
    const rightIndex = currencyOrder.indexOf(right.currency);
    return (leftIndex === -1 ? 99 : leftIndex) - (rightIndex === -1 ? 99 : rightIndex);
  })
);

// ── the conversion preview ───────────────────────────────────────────────────
// Before any ledger row is written, the couple has to be able to CHECK the
// conversion: what goes in, what comes out, at exactly what rate, from where,
// and how old that rate is. These helpers exist so the panel can show the
// arithmetic rather than only its conclusion.

/** Whole cents, half-up on positives — the same answer the server's Decimal
 *  quantisation gives, so the previewed figure is the figure that gets
 *  written. The server re-derives it either way and is the authority. */
const roundCents = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

/** The rate at FULL precision. `toFixed(2)` here would show "1.37" for a rate
 *  of 1.36666667 — a number the couple could not reconcile against either the
 *  source or the converted amount. */
const formatRate = (rate) => {
  if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return '—';
  const value = Number(rate);
  const fixed = value.toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
  return fixed === '' ? '0' : fixed;
};

/** How stale the fetched rate is. The cache lives 12h, so "as of <ECB date>"
 *  on its own lets a rate pulled this morning read as one pulled this second.
 *  Returns null when the age is genuinely unknown — which is said out loud
 *  rather than rendered as "just now". */
const formatRateAge = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return null;
  const value = Math.max(0, Math.round(Number(seconds)));
  if (value < 90) return 'just now';
  if (value < 5400) return `${Math.round(value / 60)} min ago`;
  const hours = Math.round(value / 3600);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
};

/**
 * Validate an agreed rate exactly as strictly as the server does, so the
 * button is disabled before the request rather than the request being
 * refused after it. The server remains the authority — this is a courtesy,
 * never the guard.
 *
 * Returns {state: 'empty'|'ok'|'error', value, message}. An empty box means
 * "use the fetched rate"; it is not an error.
 */
const parseAgreedRate = (raw) => {
  const text = String(raw ?? '').trim();
  if (!text) return { state: 'empty', value: null, message: '' };
  if (!/^\d*\.?\d+$/.test(text)) return { state: 'error', value: null, message: 'Enter a positive number.' };
  const value = Number(text);
  if (!Number.isFinite(value) || value <= 0) return { state: 'error', value: null, message: 'The rate must be greater than zero.' };
  if (value < 0.00000001 || value > 1000000) return { state: 'error', value: null, message: 'That rate is outside any plausible range.' };
  const decimals = (text.split('.')[1] || '').length;
  if (decimals > 8) return { state: 'error', value: null, message: 'At most 8 decimal places.' };
  return { state: 'ok', value, message: '' };
};

const settlementFormFromBalance = (balance) => ({
  occurred_on: new Date().toISOString().split('T')[0],
  amount: balance?.amount ?? '',
  currency: balance?.currency ?? '',
  paid_by: balance?.direction === 'nav_owes_kate' ? 'navjeev' : balance?.direction === 'kate_owes_nav' ? 'kate' : '',
  paid_to: balance?.direction === 'nav_owes_kate' ? 'kate' : balance?.direction === 'kate_owes_nav' ? 'navjeev' : '',
  notes: balance?.currency ? `Settling open ${balance.currency} balance.` : '',
});

const TransactionDetailSheet = ({ record, history, loading, error, onClose, onRetry }) => {
  // Escape-to-close, and lock the page behind the sheet so a long history does
  // not scroll the ledger underneath it on mobile.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  if (!record) return null;
  const events = history?.events || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Details for ${record.description}`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:rounded-[28px]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              <span>{record.occurred_on}</span>
              <span>•</span>
              <span>{record.category || 'Uncategorized'}</span>
            </div>
            <h2 className="mt-1 truncate text-lg font-semibold text-slate-900">{record.description}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="shrink-0 rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="rounded-[22px] bg-slate-50 px-4 py-3 ring-1 ring-slate-200">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Total</span>
              <span className="text-2xl font-semibold text-slate-900">{formatMoney(record.currency, record.total_cost || 0)}</span>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-slate-500">Type</dt>
              <dd className="text-right font-medium text-slate-800">
                {describeEntryType(record)}
              </dd>
              <dt className="text-slate-500">Paid by</dt>
              <dd className="text-right font-medium text-slate-800">{record.paid_by_member?.display_name || '—'}</dd>
              {record.paid_to_member ? (
                <>
                  <dt className="text-slate-500">Paid to</dt>
                  <dd className="text-right font-medium text-slate-800">{record.paid_to_member.display_name}</dd>
                </>
              ) : null}
              <dt className="text-slate-500">Split</dt>
              <dd className="text-right font-medium text-slate-800">{record.split_method || '—'}</dd>
            </dl>
            <ul className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-sm">
              {(record.shares || []).map((share) => (
                <li key={share.member.slug} className="flex items-center justify-between gap-3">
                  <span className="text-slate-600">{share.member.display_name}</span>
                  <span className="font-medium text-slate-800">
                    {formatMoney(record.currency, share.share_amount || 0)}
                    <span className={clsx('ml-2 text-xs', Number(share.balance_delta) >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                      {Number(share.balance_delta) >= 0 ? '+' : ''}{Number(share.balance_delta || 0).toFixed(2)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            {record.notes ? <p className="mt-3 border-t border-slate-200 pt-3 text-sm text-slate-600">{record.notes}</p> : null}
          </div>

          <div className="mt-5">
            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
              <History className="h-4 w-4" /> History
            </h3>

            {loading ? (
              <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
              </p>
            ) : error ? (
              <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                <p>{error}</p>
                <button type="button" onClick={onRetry} className="mt-2 font-semibold underline">
                  Try again
                </button>
              </div>
            ) : (
              <HistoryEventList events={events} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ExpensesDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState({ balances: [], records: [], filters: { categories: [], currencies: [] }, stats: { top_categories: {} }, ai_summary: [] });
  const [loading, setLoading] = useState(true);
  /**
   * Whether the first dashboard read has come back.
   *
   * The skeletons used to be gated on `loading && !dashboard.stats`, which is
   * never true: the initial state seeds `stats` with an object, so
   * `!dashboard.stats` is false from the very first render. The gate looked
   * right, read right, and had no effect -- the screen went straight to
   * "All square" and "0 records" on a ledger holding 489 records, then
   * corrected itself once the read landed. A placeholder that states a number
   * gets believed; that is the whole reason the note asked for a skeleton.
   */
  const [hasLoaded, setHasLoaded] = useState(false);
  // True only while the window is being widened underneath a search.
  const [expanding, setExpanding] = useState(false);
  // A widening read was refused. Distinct from `expanding`: one means "in
  // flight", the other means "gave up", and the caption must never confuse
  // them. See the widening effect below.
  const [expandFailed, setExpandFailed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [search, setSearch] = useState('');
  // The search + filter controls take no room until asked for, and the ledger
  // shows COMPACT_LIMIT rows until asked for more.
  const [showFilters, setShowFilters] = useState(false);
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState(defaultExpenseForm);
  const [settlementForm, setSettlementForm] = useState(defaultSettlementForm);
  const [editingRecord, setEditingRecord] = useState(null);
  const [settleTargetCurrency, setSettleTargetCurrency] = useState(null);
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);
  // Raw text per source currency, not numbers: an agreed rate is validated
  // once, in `parseAgreedRate`, and coercing "1." or "" to a number on the way
  // in would hide the very input the validator exists to refuse.
  const [customRates, setCustomRates] = useState({});
  const [rebasing, setRebasing] = useState(false);
  // Set when the server refuses a confirmation because the exchange rates moved
  // after the preview was drawn (409 `fx_rates_changed`). Nothing was written;
  // the refreshed dashboard is already on screen and this says why the numbers
  // changed under the couple's eyes. Cleared whenever the plan changes.
  const [rateChangeNotice, setRateChangeNotice] = useState('');
  // Transaction details + its history. Fetched per record, only when opened.
  const [detailRecord, setDetailRecord] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Recovery: the deleted-entry listing, the drawer that shows it, and the
  // confirmation that now stands in front of every delete.
  const [deletedListing, setDeletedListing] = useState({ entries: [], count: 0, total: 0, truncated: false });
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedError, setDeletedError] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /**
   * One live Undo handle per toast, keyed by that toast's own id.
   *
   * This was a single `deletedItemRef`, and a single ref cannot describe two
   * deletes. Delete "Ramen night", delete "Sushi lunch" a second later, press
   * Undo on the Ramen toast still on screen -- and the ref had already been
   * overwritten, so the ledger got Sushi back and Ramen stayed deleted. A
   * correct-looking control with the wrong effect, on two people's money.
   * The shared 5.1s expiry timer failed the same way from the other end: the
   * first delete's timer cleared the handle the second toast was still using,
   * so its Undo silently did nothing.
   *
   * Each toast now owns its record id and its own timer, so N toasts can be
   * on screen at once and each one means exactly what it says.
   */
  const undoHandlesRef = useRef(new Map());
  const undoSeqRef = useRef(0);
  const searchInputRef = useRef(null);
  // The page behind a dialog. `useDialogA11y` marks it inert while a dialog is
  // open, so the dialogs themselves portal to document.body and are rendered
  // outside this element.
  const backgroundRef = useRef(null);
  // Where focus lands when the control that opened a dialog is gone by the
  // time it closes — see the effect below.
  const ledgerHeadingRef = useRef(null);

  const displayedRecords = useMemo(() => dashboard.records || [], [dashboard.records]);
  const settlementPreview = dashboard.settlement_preview || null;
  const activeBalances = sortBalances(settlementPreview?.active_balances || []);

  /**
   * Everything the confirmation needs, computed from the chosen target and
   * whatever agreed rates have been typed: per-balance converted amounts, the
   * rate actually applied to each, and the resulting net.
   *
   * The server recomputes all of it and is the authority — it refuses a rate
   * this cannot even represent, and it refuses a conversion that would round
   * a debt to zero. This exists so the couple can see the arithmetic BEFORE
   * they confirm it, which is the whole point of a preview.
   */
  const conversionPlan = useMemo(() => {
    const option = settlementPreview?.fx?.combined_options?.find((o) => o.currency === settleTargetCurrency);
    if (!option) return null;
    // A server older than this UI sends no `legs`. Degrade to the balances we
    // do have rather than rendering an empty panel.
    const source = (option.legs && option.legs.length)
      ? option.legs
      : (settlementPreview?.active_balances || []).map((b) => ({
        currency: b.currency,
        amount: b.amount,
        direction: b.direction,
        converted_amount: b.currency === settleTargetCurrency ? b.amount : null,
        rate: b.currency === settleTargetCurrency ? 1 : null,
        needs_conversion: b.currency !== settleTargetCurrency,
      }));

    let blockingError = '';
    let anyAgreed = false;
    let net = 0;
    let netKnown = true;

    const legs = source.map((leg) => {
      const parsed = parseAgreedRate(customRates[leg.currency]);
      const agreed = Boolean(leg.needs_conversion) && parsed.state === 'ok';
      if (leg.needs_conversion && parsed.state === 'error' && !blockingError) {
        blockingError = 'Fix the agreed rate above before moving any balances.';
      }
      if (agreed) anyAgreed = true;
      const appliedRate = agreed ? parsed.value : (leg.rate ?? null);
      const convertedAmount = agreed
        ? roundCents(Number(leg.amount) * parsed.value)
        : (leg.converted_amount ?? null);
      if (convertedAmount === null) {
        netKnown = false;
      } else {
        net += leg.direction === 'nav_owes_kate' ? convertedAmount : -convertedAmount;
        // The same refusal the server makes, said before the request rather
        // than after it: a leg that rounds to nothing would clear a debt in
        // one currency and re-open it as 0.00 in another, which is the debt
        // disappearing.
        if (leg.needs_conversion && roundCents(convertedAmount) === 0 && Number(leg.amount) > 0 && !blockingError) {
          blockingError = `${leg.currency} ${Number(leg.amount).toFixed(2)} is worth less than one cent in ${settleTargetCurrency} at that rate — the debt would round away.`;
        }
      }
      return {
        currency: leg.currency,
        amount: Number(leg.amount),
        direction: leg.direction,
        needsConversion: Boolean(leg.needs_conversion),
        fetchedRate: leg.rate ?? null,
        appliedRate,
        convertedAmount,
        isAgreed: agreed,
        rateError: leg.needs_conversion ? parsed.message : '',
      };
    });

    const rounded = roundCents(net);
    return {
      legs,
      // Server-derived fingerprint of this target-specific balance plan. It
      // covers the source balances, target, converted legs and final net; the
      // POST echoes it back so a newer ledger state cannot be substituted
      // between this panel and the tap.
      planDigest: option.plan_digest,
      anyAgreed,
      blockingError,
      netAmount: netKnown ? Math.abs(rounded) : option.amount,
      netDirection: netKnown
        ? (rounded > 0 ? 'nav_owes_kate' : rounded < 0 ? 'kate_owes_nav' : 'settled')
        : option.direction,
      agreedRates: legs.reduce((acc, leg) => (
        leg.isAgreed ? { ...acc, [leg.currency]: leg.appliedRate } : acc
      ), {}),
    };
  }, [settlementPreview, settleTargetCurrency, customRates]);

  // A rate agreed for one settle-to currency means nothing for another.
  useEffect(() => {
    setCustomRates({});
    setRateChangeNotice('');
  }, [settleTargetCurrency, showCurrencyChooser]);

  const filtersActive = Boolean(search.trim() || selectedCategory || selectedCurrency || selectedType);

  const matchingRecords = useMemo(() => (
    displayedRecords.filter((record) => (
      matchesSearch(record, search)
      && (!selectedCategory || (record.category || 'Uncategorized') === selectedCategory)
      && (!selectedCurrency || record.currency === selectedCurrency)
      && matchesEntryType(record, selectedType)
    ))
  ), [displayedRecords, search, selectedCategory, selectedCurrency, selectedType]);

  // A search or a filter overrides the compact cap. Someone who has narrowed
  // the ledger to four rows is not helped by being told there are "more" of
  // them hidden -- and a cap that silently trimmed a result set would make the
  // match count a lie.
  const visibleRecords = (filtersActive || showAllRecords)
    ? matchingRecords
    : matchingRecords.slice(0, COMPACT_LIMIT);
  const hiddenCount = matchingRecords.length - visibleRecords.length;

  // The window the ledger is reading from. It is not necessarily the whole
  // ledger, and the caption says so rather than implying the filters searched
  // rows that were never fetched.
  const loadedCount = displayedRecords.length;
  const totalRecords = dashboard.stats?.total_records ?? loadedCount;
  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
  const searchingPartialLedger = totalRecords > loadedCount;
  // "Still loading" has to stop being true the moment loading stops. A failed
  // widen used to leave the caption saying it forever, which is the one thing
  // a ledger caption must never do: it made "we could not read the rest" and
  // "we are still reading the rest" the same sentence.
  const stillLoadingLedger = (searchingPartialLedger || expanding) && !expandFailed;
  const partialAndStuck = expandFailed && searchingPartialLedger;
  const ledgerHoldsSuffix = searchingPartialLedger
    ? ` The ledger holds ${totalRecords}.${expandFailed ? ' The rest could not be loaded.' : ''}`
    : '';
  // Before the first read lands there is no honest count to state. "Showing
  // no transactions." is the same false zero as "0 records" -- a confident
  // claim about a 489-record ledger, made by a screen that has not read it.
  const ledgerCaption = !hasLoaded
    ? 'Loading transactions…'
    : filtersActive
    ? (stillLoadingLedger
      ? `${plural(matchingRecords.length, 'match', 'matches')} so far — still loading the remaining ${totalRecords - loadedCount} of ${totalRecords}.`
      : partialAndStuck
        // Deliberately not "N matches in M transactions." — that reads as a
        // verdict on the whole ledger, and this one is a verdict on a fraction
        // of it. Say which fraction, and say the rest is missing.
        ? `${plural(matchingRecords.length, 'match', 'matches')} in the ${loadedCount} of ${totalRecords} transactions loaded — the rest could not be loaded, so this is not the whole ledger.`
        : `${plural(matchingRecords.length, 'match', 'matches')} in ${plural(loadedCount, 'transaction', 'transactions')}.`)
    : hiddenCount > 0
      ? `Showing the ${visibleRecords.length} most recent of ${loadedCount}.${ledgerHoldsSuffix}`
      : `Showing ${loadedCount === 0 ? 'no' : `all ${loadedCount}`} ${loadedCount === 1 ? 'transaction' : 'transactions'}.${ledgerHoldsSuffix}`;

  const fetchDashboard = useCallback(async (windowSize = FETCH_LIMIT, { background = false } = {}) => {
    try {
      // A background widening keeps the current rows on screen. Flipping the
      // main `loading` flag would replace the results someone is mid-read of
      // with a "Loading..." line, which reads as their search having failed.
      if (background) setExpanding(true); else setLoading(true);
      // No search/category/currency params: narrowing happens in the browser
      // now, over this window. One fetch, and the filters answer instantly
      // instead of round-tripping on every keystroke. The server's own
      // `search` matches description ONLY, so pushing the query back to it
      // would quietly stop matching category, currency, payer and payee --
      // which is why the window grows instead (see `loadEntireLedger`).
      const params = new URLSearchParams({ limit: String(windowSize) });
      const response = await fetch(`${ENDPOINTS.EXPENSES_DASHBOARD}?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (handleAuthError(response)) return;
      if (!response.ok) throw new Error('Failed to load expenses');
      setDashboard(await response.json());
      return true;
    } catch (error) {
      console.error(error);
      toast.error('Could not load expenses.');
      // The caller needs to know. A widening read that failed silently is
      // what left the page claiming to be "still loading" forever.
      return false;
    } finally {
      if (background) setExpanding(false); else setLoading(false);
      // Set on failure too: a first read that errors must fall through to the
      // real empty/error states, not leave the page pulsing forever.
      setHasLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  /**
   * Searching has to search the whole ledger, not the first page of it.
   *
   * The compact list reads a FETCH_LIMIT window, which is the right default:
   * one request, and the filters answer without a round-trip per keystroke.
   * But this household's ledger is already 489 rows. A search that quietly
   * looked at 200 of them would answer "no matches" for a real transaction --
   * the most confidence-destroying answer a ledger can give, because it is
   * indistinguishable from the money never having been recorded.
   *
   * So the first time the user actually searches or filters (or asks to see
   * everything), the window grows to the whole ledger and stays there. Cost is
   * one extra request in the session, and only for someone who asked a
   * question the window could not honestly answer.
   */
  const wantsEntireLedger = filtersActive || showAllRecords;
  const ledgerIsPartial = totalRecords > loadedCount;
  const expandRequestedRef = useRef(false);

  /**
   * A rejected widen must be retryable.
   *
   * The ref was set BEFORE the request and never cleared on failure, so one
   * failed response permanently disabled the widen: search, filter, Show all
   * and Refresh all found `expandRequestedRef.current === true` and did
   * nothing, while the caption went on saying "still loading" for a request
   * that had already been refused. The page looked busy forever and quietly
   * answered every subsequent search from a fraction of the ledger.
   *
   * So: the ref is released on failure, and `expandFailed` — not the ref —
   * is what stops the effect from retrying on its own. Anything that clears
   * it (the Retry button, Refresh, or the user asking a new question) is a
   * retry; nothing retries in a loop behind their back.
   */
  useEffect(() => {
    if (!wantsEntireLedger || !ledgerIsPartial) return;
    if (expandRequestedRef.current || expandFailed) return;
    expandRequestedRef.current = true;
    // Never ask for a *narrower* window than the default: `total_records`
    // can legitimately be smaller than FETCH_LIMIT, and requesting it
    // verbatim would shrink the page instead of widening it.
    Promise.resolve(fetchDashboard(Math.max(totalRecords, FETCH_LIMIT), { background: true }))
      .then((succeeded) => {
        if (succeeded) return;
        expandRequestedRef.current = false;
        setExpandFailed(true);
      });
  }, [wantsEntireLedger, ledgerIsPartial, totalRecords, fetchDashboard, expandFailed]);

  // Asking a different question is asking again. Someone who edits their
  // search, changes a filter or hits "Show all" after a failed widen is
  // plainly not content with the partial answer.
  useEffect(() => {
    setExpandFailed(false);
  }, [search, selectedCategory, selectedCurrency, selectedType, showAllRecords]);

  const retryExpand = useCallback(() => {
    expandRequestedRef.current = false;
    setExpandFailed(false);
  }, []);

  /**
   * The recovery list. Fetched on mount and after every delete or restore, so
   * the entry point survives the Undo toast expiring AND a page reload -- which
   * is the whole reason it exists. Its own endpoint: the dashboard read must
   * never return a deleted row.
   */
  const fetchDeleted = useCallback(async () => {
    setDeletedLoading(true);
    setDeletedError('');
    try {
      const response = await fetch(`${ENDPOINTS.EXPENSES_DELETED}?limit=25`, {
        headers: getAuthHeaders(),
      });
      if (handleAuthError(response)) return;
      if (!response.ok) throw new Error('Could not load deleted entries');
      setDeletedListing(await response.json());
    } catch (error) {
      console.error(error);
      // Say it in the drawer rather than as a toast: a background refresh that
      // shouts at someone who is not looking for it is just noise.
      setDeletedError(error.message || 'Could not load deleted entries');
    } finally {
      setDeletedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeleted();
  }, [fetchDeleted]);

  // Every outstanding Undo expiry belongs to a toast that is gone once this
  // component is.
  const undoHandles = undoHandlesRef.current;
  useEffect(() => () => {
    undoHandles.forEach((handle) => clearTimeout(handle.timer));
    undoHandles.clear();
  }, [undoHandles]);

  /**
   * Catch the keyboard user a closing dialog would otherwise drop.
   *
   * `useDialogA11y` hands focus back to whatever opened the dialog, but it
   * correctly refuses to focus an element that is no longer in the document —
   * and both of these dialogs routinely destroy their own launcher: confirming
   * a delete removes that row's Delete button from the ledger, and restoring
   * the last deleted entry removes the "Recently deleted" button entirely. In
   * both cases focus fell to <body>, so tabbing resumed from the top of the
   * page. Land on the ledger heading instead: it is where both launchers live
   * and where the affected transaction now is.
   */
  const dialogWasOpen = useRef(false);
  const anyDialogOpen = showDeleted || Boolean(pendingDelete);

  /**
   * Every overlay, not just the two that mark the page inert.
   *
   * `useDialogA11y` only reaches the delete confirmation and the recovery
   * drawer. ExpenseModal, SettlementModal and the transaction detail sheet all
   * render a `fixed inset-0 z-50` layer over a page they leave fully tabbable,
   * so a fixed bottom action bar would keep drawing on top of their backdrop
   * and keep taking Tab. Not rendering the bar at all while anything is open
   * is the one version of "invisible and unfocusable" that no z-index or
   * stacking context can defeat.
   */
  const anyOverlayOpen = anyDialogOpen
    || showExpenseModal
    || showSettlementModal
    || Boolean(detailRecord);
  useEffect(() => {
    if (anyDialogOpen) {
      dialogWasOpen.current = true;
      return;
    }
    if (!dialogWasOpen.current) return;
    dialogWasOpen.current = false;
    if (document.activeElement === document.body) {
      ledgerHeadingRef.current?.focus();
    }
  }, [anyDialogOpen]);

  // Filtering is local, so there is nothing to debounce and nothing to apply:
  // the list narrows as the characters land.
  const handleSearchChange = (e) => setSearch(e.target.value);

  const toggleFilters = () => {
    setShowFilters((open) => {
      const next = !open;
      // Opening the panel puts the caret in the search box: revealing a search
      // field and then making the user tap it again is two taps for one
      // intention.
      if (next) window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return next;
    });
  };

  const resetExpenseForm = () => {
    setEditingRecord(null);
    setExpenseForm(defaultExpenseForm);
  };

  const closeExpenseModal = () => {
    setShowExpenseModal(false);
    resetExpenseForm();
  };

  const openNewExpenseModal = () => {
    resetExpenseForm();
    setShowExpenseModal(true);
  };

  const closeSettlementModal = () => {
    setShowSettlementModal(false);
    setSettlementForm(defaultSettlementForm);
    setEditingRecord(null);
  };

  const openNewSettlementModal = () => {
    setEditingRecord(null);
    setSettlementForm(defaultSettlementForm);
    setShowSettlementModal(true);
  };

  const openBalanceSettlementModal = (balance) => {
    setEditingRecord(null);
    setSettlementForm(settlementFormFromBalance(balance));
    setShowSettlementModal(true);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setSelectedCurrency('');
    setSelectedType('');
  };

  const openEditModal = (record) => {
    // Both legs of a currency conversion are immutable, not just the
    // fx_settlement one: the clearing payment is what zeroes the source
    // currency, so editing it desyncs the two currencies. The server refuses
    // either leg; this is the friendly early exit.
    if (isConversionLeg(record)) {
      toast('Conversion records are immutable — delete either leg (both go together) and re-run the conversion instead.');
      return;
    }
    if (record.entry_type === 'payment') {
      setSettlementForm({
        occurred_on: record.occurred_on,
        amount: record.total_cost ?? '',
        currency: record.currency,
        paid_by: record.paid_by_member?.slug || 'kate',
        paid_to: record.paid_to_member?.slug || 'navjeev',
        notes: record.notes || '',
      });
      setEditingRecord(record);
      setShowSettlementModal(true);
      return;
    }

    const kateShare = record.shares.find((share) => share.member.slug === 'kate')?.share_amount ?? '';
    const navShare = record.shares.find((share) => share.member.slug === 'navjeev')?.share_amount ?? '';
    setExpenseForm({
      occurred_on: record.occurred_on,
      description: record.description,
      category: record.category || 'General',
      total_cost: record.total_cost ?? '',
      currency: record.currency,
      split_type: record.split_method === 'equal' ? 'equal' : 'custom',
      paid_by: record.paid_by_member?.slug || 'navjeev',
      kate_share: kateShare,
      nav_share: navShare,
      kate_percentage: '',
      nav_percentage: '',
      notes: record.notes || '',
    });
    setEditingRecord(record);
    setShowExpenseModal(true);
  };

  const loadHistory = useCallback(async (recordId) => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await fetch(ENDPOINTS.EXPENSES_ENTRY_HISTORY(recordId), {
        headers: getAuthHeaders(),
      });
      if (handleAuthError(response)) return;
      if (!response.ok) throw new Error('Could not load history');
      setHistory(await response.json());
    } catch (err) {
      // Say what failed. The detail sheet keeps rendering the record itself, so
      // a history outage never hides the transaction.
      setHistoryError(err.message || 'Could not load history');
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const openDetail = (record) => {
    setDetailRecord(record);
    setHistory(null);
    setHistoryError('');
    loadHistory(record.id);
  };

  const closeDetail = () => {
    setDetailRecord(null);
    setHistory(null);
    setHistoryError('');
  };

  /**
   * Restore one soft-deleted entry, from the Undo toast or the recovery drawer.
   *
   * IDEMPOTENT by design. Two taps, two phones, or a retry after a flaky
   * response all converge on the same end state: the record is back, exactly
   * once. The server refuses a second restore with 400 "Record is not deleted",
   * and that is not an error to show anyone -- the entry they asked to recover
   * is recovered. Treat it as success, refresh, and say so plainly.
   */
  const restoreEntry = useCallback(async (recordId) => {
    const response = await fetch(ENDPOINTS.EXPENSES_ENTRY_RESTORE(recordId), {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (handleAuthError(response)) return { cancelled: true };

    if (response.status === 400) {
      const body = await response.json().catch(() => ({}));
      if (String(body.detail || '').toLowerCase().includes('not deleted')) {
        await fetchDashboard();
        await fetchDeleted();
        return { alreadyRestored: true };
      }
      throw new Error(body.detail || 'Restore failed');
    }
    if (!response.ok) throw new Error('Restore failed');

    const result = await response.json();
    setDashboard(result.dashboard);
    await fetchDeleted();
    return { record: result.record };
  }, [fetchDashboard, fetchDeleted]);

  // Delete is a two-step now: name the transaction, warn when a currency
  // conversion means two rows are going, and only then write.
  const requestDelete = (record) => setPendingDelete(record);

  const confirmDelete = async () => {
    const record = pendingDelete;
    if (!record) return;
    try {
      setDeleting(true);
      const response = await fetch(`${ENDPOINTS.EXPENSES_ENTRIES}/${record.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (handleAuthError(response)) return;
      if (!response.ok) throw new Error('Failed to delete record');
      const result = await response.json();
      setDashboard(result.dashboard);
      setPendingDelete(null);
      // The recovery list is refreshed BEFORE the toast is shown, so the
      // "Recently deleted" entry point is already there when the toast goes.
      await fetchDeleted();

      // Bound to THIS delete: the id is captured here, never read back out of
      // shared state at click time, so a later delete cannot redirect it.
      undoSeqRef.current += 1;
      const toastId = `undo-delete-${record.id}-${undoSeqRef.current}`;
      const handle = { recordId: record.id, timer: null };
      undoHandlesRef.current.set(toastId, handle);
      // This toast's own expiry, cleared by this toast's own id. It only ever
      // disarms the handle it was created for.
      handle.timer = setTimeout(() => {
        undoHandlesRef.current.delete(toastId);
      }, 5100);

      toast(
        // No `t` argument: the toast id is the one created above, so the
        // handler cannot be handed a different toast's identity.
        () => (
          <span className="flex items-center gap-3">
            {/* A conversion is deleted as a pair — say so, or the second row
                disappearing looks like a bug. Undo restores both too. */}
            <span>{isConversionLeg(record) ? 'Conversion deleted — both legs.' : 'Entry deleted.'}</span>
            <button
              type="button"
              // Names the transaction. Two Undo toasts stacked up otherwise
              // read as two identical buttons, which is the same ambiguity in
              // the accessibility tree that the shared ref was in the code.
              aria-label={`Undo delete of ${record.description}`}
              onClick={async () => {
                toast.dismiss(toastId);
                const pending = undoHandlesRef.current.get(toastId);
                if (!pending) return;
                clearTimeout(pending.timer);
                undoHandlesRef.current.delete(toastId);
                try {
                  // Soft delete on the backend: restore brings back the exact
                  // record — same id, same shares — no lossy re-create.
                  const outcome = await restoreEntry(pending.recordId);
                  if (outcome?.cancelled) return;
                  toast.success('Entry restored.');
                } catch (error) {
                  console.error(error);
                  toast.error('Could not restore the entry.');
                }
              }}
              // 44px minimum in both axes. This was a px-3/py-1/text-xs chip
              // roughly 24px tall — the one control standing between a
              // mis-tap and a vanished transaction, and the smallest target
              // on the page.
              className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:border-slate-800 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
            >
              Undo
            </button>
          </span>
        ),
        { id: toastId, duration: 5000 }
      );
    } catch (error) {
      console.error(error);
      toast.error('Could not delete entry.');
    } finally {
      setDeleting(false);
    }
  };

  const restoreFromDrawer = async (entry) => {
    const outcome = await restoreEntry(entry.restore_id);
    if (outcome?.cancelled) return outcome;
    if (outcome?.alreadyRestored) {
      toast('That entry was already back in the ledger.');
    } else {
      toast.success(entry.is_fx_pair ? 'Conversion restored — both legs.' : 'Entry restored.');
    }
    return outcome;
  };

  const handleRebase = async (targetCurrency) => {
    if (rebasing || !targetCurrency) return;
    // Belt and braces: the button is already disabled, but a rate that cannot
    // be represented must never reach the ledger by any route.
    if (conversionPlan?.blockingError) {
      toast.error(conversionPlan.blockingError);
      return;
    }
    try {
      setRebasing(true);
      setRateChangeNotice('');
      const agreed = conversionPlan?.agreedRates || {};
      // The fingerprint of the rates THESE figures were computed from, handed
      // straight back to the server. It is not a rate and cannot become one:
      // the server recomputes the same fingerprint from its own snapshot and
      // only ever compares, so a stale preview is refused rather than silently
      // repriced. Without it the couple could read one set of numbers and
      // confirm another — FX_CACHE is per-process with a 12h TTL, so the dyno
      // answering the confirmation need not be the one that drew the preview.
      const rateDigest = settlementPreview?.fx?.rate_digest;
      const planDigest = conversionPlan?.planDigest;
      const response = await fetch(ENDPOINTS.EXPENSES_REBASE, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          target_currency: targetCurrency,
          // Omitted entirely when nothing was agreed, so the ordinary path
          // sends exactly the request it always did.
          ...(Object.keys(agreed).length ? { custom_rates: agreed } : {}),
          ...(rateDigest ? { rate_digest: rateDigest } : {}),
          ...(planDigest ? { plan_digest: planDigest } : {}),
        }),
      });
      if (handleAuthError(response)) return;
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const detail = err.detail;
        // The rate-integrity refusals answer with an object, not a string.
        if (detail && typeof detail === 'object') {
          if (detail.dashboard) setDashboard(detail.dashboard);
          if (response.status === 409) {
            // Deliberately NOT a dead end: the refreshed figures are already
            // on screen, the chosen currency and any agreed rates are kept,
            // and the panel stays open so the couple can read the new numbers
            // and confirm again.
            const planChanged = detail.code === 'fx_plan_changed';
            setRateChangeNotice(detail.message || (
              planChanged
                ? 'The balances or conversion plan changed — check the new figures before confirming.'
                : 'The exchange rates changed — check the new figures before confirming.'
            ));
            toast.error(planChanged ? 'Plan changed — nothing was moved.' : 'Rates changed — nothing was moved.');
            return;
          }
          throw new Error(detail.message || 'Balance move failed');
        }
        throw new Error(detail || 'Balance move failed');
      }
      const result = await response.json();
      setDashboard(result.dashboard);
      setSettleTargetCurrency(null);
      setShowCurrencyChooser(false);
      toast.success(`Open balances moved to ${targetCurrency}`);
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Could not move balances.');
    } finally {
      setRebasing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f7f3ee_0%,#f4f6f8_46%,#eef2f6_100%)] text-slate-900">
      <div ref={backgroundRef} className="mx-auto max-w-6xl px-4 py-5 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,#faf7f2_0%,#f8fbff_56%,#f2f7f7_100%)] px-4 py-5 text-slate-900 shadow-[0_32px_100px_rgba(15,23,42,0.10)] sm:px-6 sm:py-7">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(15,23,42,0.03),_transparent_30%),radial-gradient(circle_at_bottom_left,_rgba(14,165,233,0.05),_transparent_30%)]" />
          <div className="relative">
            <div className="max-w-3xl">
              <div className="mb-5">
                <button
                  onClick={() => navigate('/')}
                  className="mb-5 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
                >
                  ← Back to Home
                </button>
                <p className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                  <Landmark className="h-3.5 w-3.5" />
                  Shared Household Ledger
                </p>
                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Shared Expenses</h1>

                {/* What is owed, right at the top — the one thing worth
                    knowing on open. Each row settles in place. */}
                {!hasLoaded ? (
                  <div className="mt-4 space-y-2" aria-busy="true">
                    <div className="h-4 w-40 animate-pulse rounded-full bg-slate-200" />
                    <div className="h-9 w-64 animate-pulse rounded-full bg-slate-200" />
                  </div>
                ) : activeBalances.length ? (
                  <div className="mt-4" data-testid="owed-summary">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Currently owed
                    </p>
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {activeBalances.map((balance) => (
                        <li key={balance.currency}>
                          <button
                            type="button"
                            onClick={() => openBalanceSettlementModal(balance)}
                            title={`Settle the ${balance.currency} balance`}
                            className="inline-flex min-h-[44px] min-w-[44px] items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-left shadow-sm transition hover:border-slate-500 hover:bg-slate-50"
                          >
                            <span className={clsx('rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em]', getCurrencyTone(balance.currency))}>
                              {balance.currency}
                            </span>
                            <span className="text-base font-semibold text-slate-900">
                              {formatMoney(balance.currency, balance.amount)}
                            </span>
                            <span className="text-xs text-slate-500">
                              {balance.direction === 'nav_owes_kate' ? 'Nav → Kate' : 'Kate → Nav'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {activeBalances.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setShowCurrencyChooser((value) => !value)}
                        className="mt-3 inline-flex min-h-[44px] min-w-[44px] items-center gap-2 rounded-full border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Settle everything in one currency
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-4 inline-flex min-h-[44px] items-center rounded-full bg-emerald-50 px-4 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-200">
                    All square — nothing owed in any currency.
                  </p>
                )}
              </div>
            </div>

            {/* The conversion panel is this button's disclosed content, so it
                sits directly under it rather than in a second column. That
                column previously also carried a summary card repeating the
                balance already shown above it and a record count the ledger
                caption already states -- removed, since a panel that restates
                its neighbours is just a wider page. */}
            {showCurrencyChooser && activeBalances.length > 1 ? (
              <section data-testid="fx-conversion-panel" className="mt-4 max-w-3xl rounded-[28px] border border-slate-200 bg-[#fffaf0] p-4 shadow-sm ring-1 ring-amber-100">
                <p className="text-sm leading-6 text-slate-600">
                  Choose the currency to settle everything into. The conversion is shown before any ledger records are written.
                </p>
                      {showCurrencyChooser && settlementPreview?.fx?.combined_options?.length ? (
                        <>
                          <div className="mt-3">
                            <label className="grid gap-1.5">
                              <span className="text-xs font-medium text-slate-500">Settle-to currency</span>
                              <select
                                className="min-h-[44px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-base"
                                value={settleTargetCurrency || ''}
                                onChange={(e) => setSettleTargetCurrency(e.target.value || null)}
                              >
                                <option value="">Pick a target currency…</option>
                                {settlementPreview.fx.combined_options
                                  .map((opt) => (
                                    <option key={opt.currency} value={opt.currency}>
                                      {opt.currency} — total {formatMoney(opt.currency, opt.amount)}
                                    </option>
                                  ))}
                              </select>
                            </label>
                          </div>
                          {settleTargetCurrency && conversionPlan ? (() => {
                            const { legs, netAmount, netDirection, anyAgreed, blockingError } = conversionPlan;
                            const payer = netDirection === 'nav_owes_kate' ? 'Navjeev' : 'Kate';
                            const payee = netDirection === 'nav_owes_kate' ? 'Kate' : 'Navjeev';
                            const fx = settlementPreview.fx;
                            const age = formatRateAge(fx.age_seconds);
                            return (
                              <div className="mt-3 space-y-3">
                                <div className="rounded-[20px] bg-white p-4 ring-1 ring-slate-200">
                                  {/* Every source balance, its converted
                                      amount, and the exact rate in between.
                                      This panel used to say only "USD 622.77
                                      will be converted to CAD" — a sentence
                                      the couple could not check before it
                                      moved their money. */}
                                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                                    What will be converted
                                  </div>
                                  <ul className="mt-2 divide-y divide-slate-100">
                                    {legs.map((leg) => (
                                      <li key={leg.currency} className="py-3" data-testid={`fx-leg-${leg.currency}`}>
                                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                                          <span className="font-semibold text-slate-900">
                                            {formatMoney(leg.currency, leg.amount)}
                                          </span>
                                          <span aria-hidden="true" className="text-slate-400">→</span>
                                          <span className="font-semibold text-slate-900">
                                            {formatMoney(settleTargetCurrency, leg.convertedAmount)}
                                          </span>
                                          {leg.needsConversion ? null : (
                                            <span className="text-xs text-slate-500">· already in {settleTargetCurrency}</span>
                                          )}
                                        </div>
                                        {leg.needsConversion ? (
                                          <>
                                            <div className="mt-1 text-xs text-slate-500">
                                              Rate applied: 1 {leg.currency} = {formatRate(leg.appliedRate)} {settleTargetCurrency}
                                              {' '}
                                              <span className={clsx(
                                                'ml-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]',
                                                leg.isAgreed ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600',
                                              )}>
                                                {leg.isAgreed ? 'agreed rate' : 'fetched rate'}
                                              </span>
                                            </div>
                                            <label className="mt-2 grid gap-1">
                                              <span className="text-[11px] font-medium text-slate-500">
                                                Agreed {leg.currency}→{settleTargetCurrency} rate (optional — leave blank to use the fetched rate)
                                              </span>
                                              <input
                                                type="text"
                                                inputMode="decimal"
                                                aria-label={`Agreed ${leg.currency} to ${settleTargetCurrency} rate`}
                                                value={customRates[leg.currency] || ''}
                                                onChange={(e) => setCustomRates((current) => ({ ...current, [leg.currency]: e.target.value }))}
                                                placeholder={formatRate(leg.fetchedRate)}
                                                className={clsx(
                                                  'min-h-[44px] w-full rounded-2xl border bg-white px-3 py-2 text-base',
                                                  leg.rateError ? 'border-rose-400' : 'border-slate-200',
                                                )}
                                              />
                                              {leg.rateError ? (
                                                <span role="alert" className="text-[11px] font-medium text-rose-600">{leg.rateError}</span>
                                              ) : null}
                                            </label>
                                          </>
                                        ) : null}
                                      </li>
                                    ))}
                                  </ul>

                                  <div className="mt-3 border-t border-slate-200 pt-3">
                                    <div className="text-sm text-slate-500">Final net after conversion</div>
                                    <div className="mt-1 text-xl font-semibold text-slate-900" data-testid="fx-net">
                                      {netDirection === 'settled'
                                        ? `All square in ${settleTargetCurrency}`
                                        : `${payer} owes ${payee} ${formatMoney(settleTargetCurrency, netAmount)}`}
                                    </div>
                                  </div>

                                  {/* Where the rate came from and how old it
                                      is. The cache lives 12h, so saying only
                                      "as of <ECB date>" would let an
                                      hours-old rate read as a live one. */}
                                  <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2 text-[11px] leading-5 text-slate-500">
                                    <div>
                                      Fetched rates: {fx.source || 'ECB via frankfurter.app'}
                                      {fx.as_of ? ` · published ${fx.as_of}` : ''}
                                    </div>
                                    <div>
                                      {age
                                        ? `Retrieved ${age}`
                                        : 'Retrieval time unknown'}
                                      {fx.cache_ttl_hours ? ` · cached up to ${fx.cache_ttl_hours}h, so this is not a live quote` : ''}
                                    </div>
                                    {anyAgreed ? (
                                      <div className="mt-1 font-semibold text-amber-700">
                                        An agreed rate will be recorded as agreed, not as a quote from {fx.source || 'the fetched rates'}.
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                {rateChangeNotice ? (
                                  <p
                                    role="alert"
                                    data-testid="fx-rate-change-notice"
                                    className="rounded-2xl bg-amber-50 px-3 py-2 text-xs font-semibold leading-5 text-amber-800 ring-1 ring-amber-200"
                                  >
                                    {rateChangeNotice}
                                  </p>
                                ) : null}
                                {blockingError ? (
                                  <p role="alert" className="text-xs font-semibold text-rose-600">{blockingError}</p>
                                ) : null}
                                <button
                                  type="button"
                                  disabled={rebasing || Boolean(blockingError)}
                                  onClick={() => handleRebase(settleTargetCurrency)}
                                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 hover:border-slate-800 disabled:opacity-50"
                                >
                                  {rebasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                                  {rebasing ? 'Moving balances…' : `Move balances to ${settleTargetCurrency}`}
                                </button>
                              </div>
                            );
                          })() : null}
                        </>
                      ) : showCurrencyChooser ? (
                        <div className="mt-3 rounded-[20px] bg-white p-3 ring-1 ring-slate-200">
                          <div className="text-sm text-slate-500">
                            Exchange rates are temporarily unavailable. Try refreshing the page.
                          </div>
                        </div>
                      ) : null}
              </section>
            ) : null}
          </div>
        </section>

        {/* Desktop keeps this panel exactly as it was. Under `sm` it is
            replaced -- not duplicated -- by the fixed action bar at the bottom
            of the viewport, so the same two actions never appear twice in the
            accessibility tree at any one width. */}
        <div className="mt-5 hidden sm:block">
          <section className="rounded-[30px] border border-slate-200/80 bg-white/85 p-5 shadow-[0_20px_70px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Actions</p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">Keep the ledger current</h2>
            </div>
            <div className="grid gap-3">
              <button
                onClick={openNewExpenseModal}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 hover:border-slate-800"
              >
                <Plus className="h-4 w-4" />
                Add expense
              </button>
              <button
                onClick={openNewSettlementModal}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                <ArrowRightLeft className="h-4 w-4" />
                Record manual settlement
              </button>
            </div>
          </section>

        </div>

        <div className="mt-5">
          {/* p-3 under `sm`: 2x20px of card padding inside 2x16px of page
              padding was 72px of a 390px screen spent on air. */}
          <section className="rounded-[30px] border border-slate-200/80 bg-white/85 p-3 shadow-[0_20px_70px_rgba(15,23,42,0.08)] backdrop-blur sm:p-5">
            <div className="mb-4 flex flex-col gap-3">
              {/* Column on a phone: at 390px the toggle beside the heading
                  squeezed "Recent shared transactions" onto three lines. */}
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Ledger</p>
                <h2 ref={ledgerHeadingRef} tabIndex={-1} className="mt-2 text-2xl font-semibold text-slate-900 focus:outline-none">Recent shared transactions</h2>
                {/* One line that always says what is on screen and what is
                    not. `role="status"` so narrowing the list is announced --
                    a filter that changes the count silently is a filter a
                    screen-reader user cannot tell fired. */}
                <p role="status" className="mt-1 text-sm text-slate-500">{ledgerCaption}</p>
                {/* The way back. Without it the only escape from a failed
                    widen was a page reload. */}
                {partialAndStuck ? (
                  <button
                    type="button"
                    onClick={retryExpand}
                    className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100"
                  >
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                    Retry loading the rest of the ledger
                  </button>
                ) : null}
                {/* The durable recovery handle. One small pill, shown when
                    something is actually deleted -- the ledger itself stays a
                    list of live transactions, with no ghost rows and no
                    "show deleted" toggle to read past.

                    ALSO shown when the listing could not be loaded. It used to
                    be gated on `total > 0` alone, and a failed first read
                    leaves `total` at 0 -- so the one control that could open
                    the drawer and offer Retry was hidden by the very failure it
                    exists to recover from, and "we could not check" was
                    rendered identically to "nothing is deleted". Those two are
                    not the same sentence to someone looking for a transaction
                    they think they lost. */}
                {deletedListing.total > 0 || deletedError ? (
                  <button
                    type="button"
                    onClick={() => setShowDeleted(true)}
                    aria-haspopup="dialog"
                    aria-label={deletedError
                      ? 'Recently deleted — could not load, open to try again'
                      : undefined}
                    className={clsx(
                      'mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2',
                      deletedError
                        // amber-900 on amber-50 (~9:1) with an amber-600 edge
                        // (~3.2:1 on white): a failure that is legible without
                        // relying on colour alone -- the icon and the
                        // "Couldn't load" badge both say it too.
                        ? 'border-amber-600 bg-amber-50 text-amber-900 hover:bg-amber-100 focus-visible:ring-amber-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-slate-500'
                    )}
                  >
                    {deletedError
                      ? <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                      : <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />}
                    Recently deleted
                    <span className={clsx(
                      'rounded-full px-2 py-0.5 text-xs font-semibold',
                      deletedError ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-600'
                    )}>
                      {deletedError ? 'Couldn’t load' : deletedListing.total}
                    </span>
                  </button>
                ) : null}
                </div>

                {/* The whole search-and-filter apparatus, behind one control.
                    It used to be four permanently-mounted fields taking the
                    top third of a phone screen before a single transaction
                    was visible. */}
                <button
                  type="button"
                  onClick={toggleFilters}
                  aria-expanded={showFilters}
                  aria-controls="ledger-filters"
                  className={clsx(
                    'inline-flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 sm:w-auto',
                    filtersActive
                      ? 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  )}
                >
                  <Search className="h-4 w-4" aria-hidden="true" />
                  Search &amp; filter
                  {filtersActive ? (
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-900">
                      {matchingRecords.length}
                    </span>
                  ) : null}
                </button>
              </div>

              {showFilters ? (
                <div id="ledger-filters" className="grid gap-3 rounded-[24px] border border-slate-200 bg-white/80 p-3 sm:grid-cols-2 xl:grid-cols-4">
                  <label className="grid gap-1 sm:col-span-2 xl:col-span-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Search</span>
                    {/* text-base, i.e. 16px: anything smaller and iOS Safari
                        zooms the whole page in the moment this is focused. */}
                    <input
                      ref={searchInputRef}
                      type="search"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Description, category or who paid"
                      aria-label="Search transactions"
                      className="min-h-[44px] rounded-full border border-slate-200 px-4 py-2.5 text-base outline-none transition focus:border-slate-400"
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Category</span>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="min-h-[44px] rounded-full border border-slate-200 bg-white px-4 py-2.5 text-base"
                    >
                      <option value="">All categories</option>
                      {(dashboard.filters?.categories || []).map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Currency</span>
                    <select
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                      className="min-h-[44px] rounded-full border border-slate-200 bg-white px-4 py-2.5 text-base"
                    >
                      <option value="">All currencies</option>
                      {(dashboard.filters?.currencies || []).map((currency) => (
                        <option key={currency} value={currency}>{currency}</option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Type</span>
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="min-h-[44px] rounded-full border border-slate-200 bg-white px-4 py-2.5 text-base"
                    >
                      {ENTRY_TYPE_FILTERS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <div className="flex flex-wrap gap-2 sm:col-span-2 xl:col-span-4">
                    <button
                      type="button"
                      onClick={clearFilters}
                      disabled={!filtersActive}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-40"
                    >
                      <FilterX className="h-4 w-4" aria-hidden="true" />
                      Clear filters
                    </button>
                    <button
                      type="button"
                      onClick={() => { retryExpand(); fetchDashboard(); }}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      <RefreshCw className="h-4 w-4" aria-hidden="true" />
                      Refresh
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {loading ? (
              <div className="py-16 text-center text-slate-500">Loading household expenses...</div>
            ) : visibleRecords.length === 0 ? (
              /* Two different nothings, said differently. "No results for
                 sushi" and "you have not recorded anything yet" send the
                 reader in opposite directions, and a blank area sends them
                 nowhere at all. */
              filtersActive ? (
                <div className="rounded-[24px] border border-dashed border-slate-300 bg-white/70 px-5 py-10 text-center">
                  <p className="text-base font-semibold text-slate-800">No transactions match</p>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
                    {stillLoadingLedger
                      ? `Still loading the rest of the ledger (${loadedCount} of ${totalRecords} so far) — no match yet.`
                      : partialAndStuck
                      ? `Only ${loadedCount} of ${totalRecords} transactions could be loaded, so this is not a final answer — retry above and search again.`
                      : search.trim()
                        ? `Nothing in all ${loadedCount} transactions matches “${search.trim()}”` + (selectedCategory || selectedCurrency || selectedType ? ' with these filters.' : '.')
                        : 'No transaction in the ledger matches these filters.'}
                  </p>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 border-slate-900 bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <FilterX className="h-4 w-4" aria-hidden="true" />
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="rounded-[24px] border border-dashed border-slate-300 bg-white/70 px-5 py-10 text-center">
                  <p className="text-base font-semibold text-slate-800">No transactions yet</p>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
                    Add an expense or record a settlement and it will show up here.
                  </p>
                  <button
                    type="button"
                    onClick={openNewExpenseModal}
                    className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 border-slate-900 bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Add expense
                  </button>
                </div>
              )
            ) : (
              <>
                <ul className="grid list-none gap-2">
                  {visibleRecords.map((record) => (
                    <LedgerRow
                      key={record.id}
                      record={record}
                      onDetails={openDetail}
                      onEdit={openEditModal}
                      onDelete={requestDelete}
                    />
                  ))}
                </ul>

                {/* Only offered when it would actually reveal something, and
                    it says how much. "Show more" that shows nothing is worse
                    than no control. */}
                {hiddenCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => setShowAllRecords(true)}
                    className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    Show all {matchingRecords.length} transactions
                    <span className="text-slate-400">(+{hiddenCount})</span>
                  </button>
                ) : showAllRecords && !filtersActive && matchingRecords.length > COMPACT_LIMIT ? (
                  <button
                    type="button"
                    onClick={() => setShowAllRecords(false)}
                    className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    Show fewer
                  </button>
                ) : null}
              </>
            )}
          </section>
        </div>

        {/* Bottom padding for the fixed bar below. Without it the bar covers
            the last transaction in the list -- and the last row is exactly the
            one someone scrolls to the bottom to reach. A spacer rather than a
            `pb-*` on the container so the height can be the bar's real height
            plus the home-indicator inset, which no Tailwind scale has. */}
        {anyOverlayOpen ? null : (
          <div aria-hidden="true" data-testid="mobile-action-bar-spacer" className="h-safe-bar-spacer sm:hidden" />
        )}

      {/* The primary action, always reachable without scrolling.
          Rendered ONLY under `sm` (the desktop panel above is untouched) and
          ONLY when no overlay is open -- see `anyOverlayOpen`. Inside the
          background element, so the two dialogs that mark the page inert also
          neutralise it even in the frame before a re-render. */}
      {anyOverlayOpen ? null : (
        <div
          data-testid="mobile-action-bar"
          // `pb-safe-bar` is `calc(12px + env(safe-area-inset-bottom, 0px))`,
          // defined in index.css: on a phone without a home indicator it is
          // just the 12px it would have had anyway.
          className="pb-safe-bar fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-3 pt-3 shadow-[0_-8px_30px_rgba(15,23,42,0.12)] backdrop-blur sm:hidden"
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openNewExpenseModal}
              className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-slate-900 px-4 text-base font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
            >
              <Plus className="h-5 w-5" aria-hidden="true" />
              Add expense
            </button>
            <button
              type="button"
              onClick={openNewSettlementModal}
              aria-label="Record manual settlement"
              className="inline-flex h-[48px] min-h-[48px] w-[48px] min-w-[48px] shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
            >
              <ArrowRightLeft className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      </div>

      {showExpenseModal ? (
        <ExpenseModal
          editingRecord={editingRecord}
          initialForm={expenseForm}
          onClose={closeExpenseModal}
          onSaved={setDashboard}
        />
      ) : null}

      {detailRecord ? (
        <TransactionDetailSheet
          record={detailRecord}
          history={history}
          loading={historyLoading}
          error={historyError}
          onClose={closeDetail}
          onRetry={() => loadHistory(detailRecord.id)}
        />
      ) : null}

      {showSettlementModal ? (
        <SettlementModal
          editingRecord={editingRecord}
          initialForm={settlementForm}
          onClose={closeSettlementModal}
          onSaved={setDashboard}
        />
      ) : null}

      <ExpenseDeleteDialog
        open={Boolean(pendingDelete)}
        record={pendingDelete}
        busy={deleting}
        backgroundRef={backgroundRef}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />

      <RecentlyDeletedDrawer
        open={showDeleted}
        listing={deletedListing}
        loading={deletedLoading}
        error={deletedError}
        backgroundRef={backgroundRef}
        onRetry={fetchDeleted}
        onClose={() => setShowDeleted(false)}
        onRestore={restoreFromDrawer}
      />
    </div>
  );
};

export default ExpensesDashboard;
